import React, { useState, useEffect } from 'react';
import { Save, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const NewsletterPopupManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/settings/newsletter-popup-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching newsletter popup settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/settings/newsletter-popup-settings', settings);
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving newsletter popup settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">Lädt...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Newsletter-Popup Einstellungen</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Passen Sie das Erscheinungsbild und die Texte des Newsletter-Popups an
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPreview(true)}>
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Speichert...' : 'Speichern'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Behavior Settings */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Verhalten</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Popup aktivieren</Label>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={settings?.is_enabled}
                    onChange={(e) => handleChange('is_enabled', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">
                    {settings?.is_enabled ? 'Popup wird angezeigt' : 'Popup ist deaktiviert'}
                  </span>
                </div>
              </div>
              <div>
                <Label>Verzögerung (Sekunden)</Label>
                <Input
                  type="number"
                  value={settings?.show_delay_seconds || 3}
                  onChange={(e) => handleChange('show_delay_seconds', parseInt(e.target.value))}
                  min={0}
                  max={30}
                />
              </div>
            </div>
          </div>

          {/* Text Content */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Texte</h3>
            <div>
              <Label>Haupttitel</Label>
              <Input
                value={settings?.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Als Neukunde registrieren"
              />
            </div>
            <div>
              <Label>Untertitel</Label>
              <Textarea
                value={settings?.subtitle || ''}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                placeholder="Registrieren Sie sich jetzt..."
                rows={2}
              />
            </div>
            <div>
              <Label>Rabatt-Text</Label>
              <Input
                value={settings?.discount_text || ''}
                onChange={(e) => handleChange('discount_text', e.target.value)}
                placeholder="20% Willkommens-Rabatt"
              />
            </div>
            <div>
              <Label>Erfolgs-Titel</Label>
              <Input
                value={settings?.success_title || ''}
                onChange={(e) => handleChange('success_title', e.target.value)}
                placeholder="Herzlich Willkommen!"
              />
            </div>
            <div>
              <Label>Erfolgs-Untertitel</Label>
              <Input
                value={settings?.success_subtitle || ''}
                onChange={(e) => handleChange('success_subtitle', e.target.value)}
                placeholder="Vielen Dank für Ihre Registrierung"
              />
            </div>
          </div>

          {/* Color Settings */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Farben</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hintergrund Start</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.background_gradient_start || '#FEFAF5'}
                    onChange={(e) => handleChange('background_gradient_start', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.background_gradient_start || '#FEFAF5'}
                    onChange={(e) => handleChange('background_gradient_start', e.target.value)}
                    placeholder="#FEFAF5"
                  />
                </div>
              </div>
              <div>
                <Label>Hintergrund Ende</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.background_gradient_end || '#F5EDE3'}
                    onChange={(e) => handleChange('background_gradient_end', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.background_gradient_end || '#F5EDE3'}
                    onChange={(e) => handleChange('background_gradient_end', e.target.value)}
                    placeholder="#F5EDE3"
                  />
                </div>
              </div>
              <div>
                <Label>Randfarbe</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.border_color || '#C4A084'}
                    onChange={(e) => handleChange('border_color', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.border_color || '#C4A084'}
                    onChange={(e) => handleChange('border_color', e.target.value)}
                    placeholder="#C4A084"
                  />
                </div>
              </div>
              <div>
                <Label>Titelfarbe</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.title_color || '#6D5A47'}
                    onChange={(e) => handleChange('title_color', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.title_color || '#6D5A47'}
                    onChange={(e) => handleChange('title_color', e.target.value)}
                    placeholder="#6D5A47"
                  />
                </div>
              </div>
              <div>
                <Label>Button Start</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.button_gradient_start || '#C4A084'}
                    onChange={(e) => handleChange('button_gradient_start', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.button_gradient_start || '#C4A084'}
                    onChange={(e) => handleChange('button_gradient_start', e.target.value)}
                    placeholder="#C4A084"
                  />
                </div>
              </div>
              <div>
                <Label>Button Ende</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings?.button_gradient_end || '#B89968'}
                    onChange={(e) => handleChange('button_gradient_end', e.target.value)}
                    className="w-16 h-10"
                  />
                  <Input
                    value={settings?.button_gradient_end || '#B89968'}
                    onChange={(e) => handleChange('button_gradient_end', e.target.value)}
                    placeholder="#B89968"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Font Settings */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Schriftart</h3>
            <div>
              <Label>Titel Schriftart</Label>
              <select
                value={settings?.title_font_family || 'serif'}
                onChange={(e) => handleChange('title_font_family', e.target.value)}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="serif">Serif (klassisch)</option>
                <option value="sans-serif">Sans-Serif (modern)</option>
                <option value="monospace">Monospace (technisch)</option>
                <option value="cursive">Cursive (handschriftlich)</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="rounded-2xl p-8 max-w-md w-full relative shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${settings?.background_gradient_start} 0%, ${settings?.background_gradient_end} 100%)`,
              border: `2px solid ${settings?.border_color}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-4 right-4 hover:bg-gray-200 transition-all bg-white rounded-full p-2 shadow-xl"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center">
              <div
                className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${settings?.icon_gradient_start} 0%, ${settings?.icon_gradient_end} 100%)`
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="white"
                  className="w-10 h-10"
                >
                  <path d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 001.28.53l4.184-4.183a.39.39 0 01.266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0012 2.25zM8.25 8.625a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zm2.625 1.125a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z" />
                </svg>
              </div>
              <h3
                className="text-2xl font-bold mb-3"
                style={{
                  color: settings?.title_color,
                  fontFamily: settings?.title_font_family
                }}
              >
                {settings?.title}
              </h3>
              <div
                className="inline-block px-6 py-2 rounded-full mb-3 shadow-md"
                style={{
                  background: `linear-gradient(135deg, ${settings?.button_gradient_start} 0%, ${settings?.button_gradient_end} 100%)`
                }}
              >
                <p className="text-xl font-bold text-white">{settings?.discount_text}</p>
              </div>
              <p className="text-sm" style={{ color: settings?.subtitle_color }}>
                {settings?.subtitle}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewsletterPopupManager;
